import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include admin auth header
api.interceptors.request.use((config) => {
  const adminKey = localStorage.getItem('admin_key');
  if (adminKey) {
    config.headers['X-Admin-Key'] = adminKey;
  }
  return config;
});

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403) {
      // Admin authentication failed
      localStorage.removeItem('admin_key');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface User {
  id: string;
  username: string;
  full_name?: string;
  email?: string;
  telegram_id?: string;
  is_active: boolean;
  language: string;
  created_at?: string;
  last_login?: string;
  email_verified?: boolean;
}

export interface UserSubscription {
  tier: 'free' | 'bronze' | 'silver' | 'gold' | 'firooze';
  period?: 'monthly' | 'annual';
  is_active: boolean;
  started_at: string;
  /** Billing cycle end date from the latest paid invoice (non-override expiry) */
  billing_cycle_end?: string | null;
  /** @deprecated use billing_cycle_end */
  current_period_end?: string | null;
  auto_renew: boolean;
  // Temporary admin override fields
  override_until?: string | null;
  override_base_tier?: string | null;
  override_base_period?: string | null;
}

export interface SubscriptionUsage {
  date: string;
  smart_add_count: number;
  try_on_count: number;
  ai_stylist_count: number;
}

export interface SubscriptionLimits {
  max_closet_items?: number;
  max_smart_add_per_day?: number;
  max_try_on_per_day?: number;
  max_ai_stylist_per_day?: number;
  cloth_item_per_tryon?: number;
  custom_prompt_in_try_on: boolean;
  quick_ai_stylist: boolean;
  advanced_ai_stylist: boolean;
}

export interface UserDetails extends User {
  subscription: {
    user_subscription: UserSubscription;
    effective_plan: {
      tier: string;
      display_name: string;
      limits: SubscriptionLimits;
    };
  };
  usage_today: SubscriptionUsage;
  closet: {
    total_items: number;
    limit?: number;
    remaining?: number;
  };
}

export interface AdminReportsUser {
  user_id: string;
  username?: string;
  full_name?: string;
  email?: string;
  telegram_id?: string;
  email_verified?: boolean;
  subscription_tier: string;
  tier: string; // Backend uses 'tier' instead of 'subscription_tier'
  period?: string;
  is_active: boolean;
  smart_add_total: number; // Backend uses different field names
  try_on_total: number;
  total: number;
  days: number;
  last_date?: string;
  last_activity?: string;
  created_at?: string;
}

export interface UsersReport {
  items: AdminReportsUser[]; // Backend uses 'items' not 'users'
  total_users: number; // Backend uses 'total_users' not 'total_count'
  start?: string;
  end?: string;
  limit: number;
  offset: number;
  // Compatibility fields for frontend
  users?: AdminReportsUser[];
  total_count?: number;
}

export interface ActionTimeseriesPoint {
  date: string;
  // For single-action queries
  count?: number;
  // For all-actions queries
  add_item?: number;
  tryon?: number;
  get_recom?: number;
}

export interface ActionTimeseriesResponse {
  user_id: string;
  start?: string;
  end?: string;
  action?: 'add_item' | 'tryon' | 'get_recom';
  interval: 'day';
  series: ActionTimeseriesPoint[];
}

export interface WalletTransactionItem {
  id?: string;
  type: 'charge' | 'deposit' | 'refund' | string;
  amount: number;
  currency: string;
  reason?: string;
  metadata?: Record<string, any> | null;
  created_at?: string;
}

export interface ZarTransactionsResponse {
  items: WalletTransactionItem[];
  total: number;
  balance: number;
  currency: string;
  limit: number;
  offset: number;
  start?: string;
  end?: string;
}

export interface NowPaymentItem {
  id?: string;
  provider?: string; // nowpayments
  type?: 'wallet_topup' | 'subscription' | string;
  user_id?: string;
  currency?: 'ZAR' | string;
  amount?: number; // ZAR amount if topup
  order_id?: string;
  external_invoice_id?: string | null;
  invoice_url?: string | null;
  payment_id?: string | null;
  pay_currency?: string | null;
  pay_amount?: number | null;
  price_amount?: number | null;
  price_currency?: string | null;
  subscription_tier?: string | null;
  subscription_period?: string | null;
  status?: string;
  credited?: boolean;
  created_at?: string;
  updated_at?: string;
  processed_at?: string | null;
}

export interface NowPaymentsListResponse {
  items: NowPaymentItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface AdminZarTxRequest {
  type: 'deposit' | 'withdraw' | 'refund';
  amount: number;
  reason?: string;
  metadata?: Record<string, any>;
}

/** A single immutable tier-change audit record from the subscription_events collection. */
export interface SubscriptionEventItem {
  id: string;
  user_id: string;
  /** What type of change occurred */
  event_type: 'created' | 'upgraded' | 'downgraded' | 'cancelled' | 'expired' | 'admin_override';
  /** Who initiated the change */
  actor: 'user' | 'admin' | 'system';
  from_tier: string;
  to_tier: string;
  from_period?: string | null;
  to_period?: string | null;
  reason?: string | null;
  metadata: Record<string, any>;
  created_at: string;
  /**
   * When the subscription granted by this event is valid until.
   * For admin_override events: the override expiry date.
   * For payment events: the billing_cycle_end from the linked invoice.
   * Computed server-side and injected into each event response.
   */
  valid_until?: string | null;
}

export interface SubscriptionEventsResponse {
  success: boolean;
  total: number;
  limit: number;
  skip: number;
  events: SubscriptionEventItem[];
}

export interface AdminZarTxResponse {
  success: boolean;
  balance: number;
  currency: string;
  transaction: WalletTransactionItem;
}

export interface SubscriptionPlan {
  tier: 'free' | 'bronze' | 'silver' | 'gold' | 'firooze';
  display_name: string;
  description?: string;
  price_monthly?: number;
  price_annual?: number;
  currency: string;
  limits: SubscriptionLimits;
  features: string[];
  is_active: boolean;
  sort_order: number;
}

export interface ZarPackage {
  id: string;
  zar_amount: number;
  price: number;
  discount: number;
  final_price: number;
  display_name?: string;
  description?: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionConfig {
  plans: SubscriptionPlan[];
  payment_methods: {
    stripe_enabled: boolean;
    nowpayments_enabled: boolean;
    paypal_enabled: boolean;
  };
  currencies: {
    primary: string;
    supported: string[];
  };
  trial_settings: {
    trial_duration_days: number;
    trial_features: string[];
  };
  referral_settings: {
    enabled: boolean;
    reward_days: number;
  };
}

export interface SubscriptionStats {
  total_subscribers: number;
  monthly_subscribers: number;
  annual_subscribers: number;
  revenue_this_month: number;
  revenue_last_month: number;
  churn_rate: number;
  tier_distribution: Record<string, number>;
  recent_subscriptions: UserSubscriptionDetail[];
  recent_cancellations: UserSubscriptionDetail[];
}

export interface TierLimitation {
  closet_limit?: number | null;
  max_photo_models?: number | null;
  max_smart_add_per_day?: number | null;
  max_try_on_per_day?: number | null;
  max_ai_stylist_per_day?: number | null;
  max_smart_tryoff_per_day?: number | null;
  max_batch_processing_size?: number | null;
  cloth_item_per_tryon?: number | null;
  custom_prompt_in_try_on?: boolean;
  quick_ai_stylist?: boolean;
  advanced_ai_stylist?: boolean;
  init_2_ai_models?: boolean;
  profile_completion_3_ai_models?: boolean;
  custom_ai_models?: number | null;
  free_zar?: number;
  // New keys
  saved_recommendations_limit?: number | null;
  saved_looks_limit?: number | null;
  ai_stylist_save?: boolean;
  ai_style_quick_recom?: boolean;
  add_cloth_item_single?: boolean;
  add_cloth_item_batch?: boolean;
  upload_photo_model?: boolean;
  tryon?: boolean;
  // Index signature to allow dynamic feature keys
  [key: string]: boolean | number | null | undefined;
}

export interface TierLimitationsResponse {
  success: boolean;
  data: {
    tier_limitations: Record<string, TierLimitation>;
    feature_descriptions: Record<string, string>;
    api_endpoints: Record<string, string[]>;
  };
}

export interface UserSubscriptionDetail {
  user_id: string;
  username?: string;
  email?: string;
  tier: string;
  period: string;
  status: 'active' | 'cancelled' | 'expired' | 'trial';
  started_at: string;
  expires_at?: string;
  auto_renew: boolean;
  payment_method?: string;
  amount?: number;
  currency?: string;
}

// Try-On Feedback Types
export interface TryOnFeedbackItem {
  id: string;
  user_id: string;
  result_image_id?: string;
  result_image_url?: string;
  model_id: string;
  cloth_ids: string[];
  feedback_issues: string[];
  custom_message?: string;
  status: 'pending' | 'approved' | 'rejected';
  reviewed_by?: string;
  review_notes?: string;
  refunded: boolean;
  refund_amount?: number;
  refund_transaction_id?: string;
  created_at: string;
  updated_at: string;
  reviewed_at?: string;
}

export interface TryOnFeedbackListResponse {
  success: boolean;
  items: TryOnFeedbackItem[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
  counts: {
    pending: number;
    approved: number;
    rejected: number;
    total: number;
  };
}

export interface TryOnFeedbackDetail {
  success: boolean;
  feedback: TryOnFeedbackItem;
  user?: {
    id: string;
    telegram_id?: string;
    full_name?: string;
  };
  model?: {
    id: string;
    image_url?: string;
    presigned_url?: string;
    generated_by?: string;
  };
  clothes: Array<{
    id: string;
    name: string;
    image_id?: string;
    image_url?: string;
    category?: string;
  }>;
}

// Survey Types
export interface SurveyResponseItem {
  id: string;
  user_id: string;
  user_email?: string;
  user_username?: string;
  favorite_features: string[];
  least_favorite_features: string[];
  additional_feedback?: string;
  rating?: number;
  nps_score?: number;
  usage_frequency?: string;
  main_value?: string[];
  biggest_challenge?: string[];
  indispensable_feature?: string;
  completion_time_seconds?: number;
  device_type?: string;
  survey_version?: string;
  created_at: string;
  updated_at?: string;
}

export interface SurveyListResponse {
  success: boolean;
  items: SurveyResponseItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface SurveyDetailResponse {
  success: boolean;
  survey: SurveyResponseItem;
}

// API Functions
// =============== QUEST INTERFACES ===============

export interface ActionRequirement {
  action_type: string;
  target_count: number;
}

export interface AdminQuest {
  id?: string;
  quest_id: string;
  title: string;
  description: string;
  quest_type: 'action' | 'count' | 'visit';
  target_count: number;
  /** null for compound quests (use action_requirements instead) */
  action_type: string | null;
  /** Non-empty for compound quests; mutually exclusive with action_type */
  action_requirements: ActionRequirement[];
  reward_zar_credits?: number;
  reward_free_subscription_days?: number;
  reward_subscription_tier?: string;
  /** Side-effect actions triggered on completion, e.g. ['generate_photo_models'] */
  reward_actions: string[];
  prerequisite_quests: string[];
  is_onboarding: boolean;
  order: number;
  is_active: boolean;
  icon?: string;
  hint?: string;
  help_url?: string;
  help_image_url?: string;
  /** Illustration component key (e.g. 'smart_add'). When set, tapping the quest card opens an animated tutorial bottomsheet. */
  help_video_key?: string | null;
  // Claim bottomsheet
  claim_bottomsheet_enabled: boolean;
  claim_bottomsheet_title?: string | null;
  claim_bottomsheet_description?: string | null;
  claim_bottomsheet_image_url?: string | null;
  claim_bottomsheet_cta_label?: string | null;
  claim_bottomsheet_cta_action?: 'none' | 'redirect' | 'tour' | null;
  claim_bottomsheet_cta_url?: string | null;
  claim_bottomsheet_cta_tour_id?: string | null;
  /** Short text shown when user claims (only when claim bottomsheet is off) */
  claim_message?: string | null;
  /** Message shown on the celebration bottomsheet when the quest is completed */
  celebration_message?: string | null;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string;
}

export interface CreateQuestPayload {
  quest_id: string;
  title: string;
  description: string;
  quest_type: 'action' | 'count' | 'visit';
  target_count: number;
  /** Required for single-action quests; omit for compound quests */
  action_type?: string | null;
  /** Required for compound quests; omit for single-action quests */
  action_requirements?: ActionRequirement[];
  reward_zar_credits?: number;
  reward_free_subscription_days?: number;
  reward_subscription_tier?: string;
  reward_actions?: string[];
  prerequisite_quests?: string[];
  is_onboarding?: boolean;
  order?: number;
  is_active?: boolean;
  icon?: string;
  hint?: string;
  help_url?: string;
  help_image_url?: string;
  /** Illustration component key (e.g. 'smart_add'). When set, tapping the quest card opens an animated tutorial bottomsheet. */
  help_video_key?: string | null;
  // Claim bottomsheet
  claim_bottomsheet_enabled?: boolean;
  claim_bottomsheet_title?: string | null;
  claim_bottomsheet_description?: string | null;
  claim_bottomsheet_image_url?: string | null;
  claim_bottomsheet_cta_label?: string | null;
  claim_bottomsheet_cta_action?: 'none' | 'redirect' | 'tour' | null;
  claim_bottomsheet_cta_url?: string | null;
  claim_bottomsheet_cta_tour_id?: string | null;
  claim_message?: string | null;
  celebration_message?: string | null;
}

export interface QuestProgressItem {
  id?: string;
  user_id: string;
  quest_id: string;
  current_progress: number;
  target_count: number;
  status: 'locked' | 'active' | 'completed';
  completed_at?: string;
  started_at?: string;
  created_at?: string;
  updated_at?: string;
}

export interface QuestStats {
  total_quests: number;
  active_quests: number;
  onboarding_quests: number;
  completion_stats: Array<{
    quest_id: string;
    title: string;
    total_started: number;
    total_completed: number;
    completion_rate: number;
    avg_completion_time_hours?: number;
  }>;
  overall_completion_rate: number;
  total_users_with_quests: number;
  users_completed_all: number;
}

export interface QuestAnalytics {
  completion_rates: Array<{
    quest_id: string;
    title: string;
    total_started: number;
    total_completed: number;
    completion_rate: number;
    avg_completion_time_hours?: number;
  }>;
  onboarding_funnel: Array<{
    quest_id: string;
    title: string;
    order: number;
    users_reached: number;
    users_completed: number;
    drop_off_rate: number;
  }>;
  timing_analytics: Array<{
    quest_id: string;
    title: string;
    avg_hours: number;
    median_hours: number;
    min_hours: number;
    max_hours: number;
    sample_size: number;
  }>;
  dashboard_summary: {
    total_active_users: number;
    total_completed_all: number;
    overall_completion_rate: number;
    avg_quests_per_user: number;
    most_completed_quest?: string;
    least_completed_quest?: string;
  };
}

// ---------------------------------------------------------------------------
// Tour types
// ---------------------------------------------------------------------------

export type TourType = 'intro' | 'slide';
export type SlideCTAAction = 'next' | 'dismiss' | 'redirect';

export interface TourSlide {
  slide_id: string;
  order: number;
  title?: string | null;
  body?: string | null;
  image_url?: string | null;
  cta_label?: string | null;
  cta_action?: SlideCTAAction | null;
  cta_url?: string | null;
}

export interface AdminTour {
  id?: string;
  tour_id: string;
  tour_type: TourType;
  title: string;
  description?: string | null;
  is_active: boolean;
  skippable: boolean;
  // Intro-only
  intro_skip_label?: string | null;
  intro_done_label?: string | null;
  intro_next_label?: string | null;
  intro_prev_label?: string | null;
  intro_show_step_numbers: boolean;
  // Slide-only
  slides: TourSlide[];
  show_progress_dots: boolean;
  completion_redirect_url?: string | null;
  // Timestamps
  created_at?: string | null;
  updated_at?: string | null;
  deleted_at?: string | null;
}

export interface CreateTourPayload {
  tour_id: string;
  tour_type: TourType;
  title: string;
  description?: string | null;
  is_active?: boolean;
  skippable?: boolean;
  intro_skip_label?: string | null;
  intro_done_label?: string | null;
  intro_next_label?: string | null;
  intro_prev_label?: string | null;
  intro_show_step_numbers?: boolean;
  slides?: TourSlide[];
  show_progress_dots?: boolean;
  completion_redirect_url?: string | null;
}

export interface UpdateTourPayload {
  title?: string;
  description?: string | null;
  is_active?: boolean;
  skippable?: boolean;
  intro_skip_label?: string | null;
  intro_done_label?: string | null;
  intro_next_label?: string | null;
  intro_prev_label?: string | null;
  intro_show_step_numbers?: boolean;
  slides?: TourSlide[];
  show_progress_dots?: boolean;
  completion_redirect_url?: string | null;
}

export const adminApi = {
  // Authentication
  async validateAdminKey(adminKey: string): Promise<boolean> {
    // In development mode, accept any non-empty admin key
    if (process.env.REACT_APP_DEV_MODE === 'true' && adminKey.length > 0) {
      console.log('Development mode: accepting admin key');
      return true;
    }

    try {
      // Try different endpoints to validate the admin key
      const endpoints = [
        '/admin/reports/subscriptions/summary',
        '/admin/subscriptions/plans', 
        '/health' // Fallback to health check
      ];
      
      for (const endpoint of endpoints) {
        try {
          const response = await api.get(endpoint, {
            headers: { 'X-Admin-Key': adminKey }
          });
          if (response.status === 200) {
            return true;
          }
        } catch (error: any) {
          // If it's not a 403 (forbidden), continue trying other endpoints
          if (error.response?.status === 403) {
            return false; // Invalid admin key
          }
        }
      }
      
      // If no endpoint worked, assume the key is invalid
      return false;
    } catch {
      return false;
    }
  },

  // Users
  async getUsers(params?: {
    start?: string;
    end?: string;
    limit?: number;
    offset?: number;
    sort?: string;
  }): Promise<UsersReport> {
    try {
      // Use simple endpoint when no date filters (to include all users including those without actions)
      // Use reports endpoint only when date filters are specified (for date-filtered reports)
      const useSimpleEndpoint = !params?.start && !params?.end;
      
      if (useSimpleEndpoint) {
        const response = await api.get('/admin/users', { 
          params: { 
            limit: params?.limit || 1000,
            offset: params?.offset || 0,
            sort: params?.sort || undefined
          } 
        });
        const data = response.data;
        
        return {
          items: data.users || [],
          total_users: data.total_count || 0,
          limit: data.limit || (params?.limit || 1000),
          offset: data.offset || (params?.offset || 0),
          users: data.users || [], // For compatibility
          total_count: data.total_count || 0 // For compatibility
        };
      } else {
        // Use reports endpoint for date-filtered queries
        const response = await api.get('/admin/reports/users', { params });
        const data = response.data;
        
        // Map the backend response to our expected format
        return {
          ...data,
          users: data.items, // Map items to users for compatibility
          total_count: data.total_users // Map total_users to total_count for compatibility
        };
      }
    } catch (error: any) {
      // If the admin endpoint doesn't exist, return mock data for development
      if (error.response?.status === 404) {
        console.warn('Admin users endpoint not found, returning mock data');
        return {
          items: [],
          total_users: 0,
          limit: params?.limit || 20,
          offset: params?.offset || 0,
          users: [], // For compatibility
          total_count: 0 // For compatibility
        };
      }
      throw error;
    }
  },

  async getUserDetails(userId: string): Promise<UserDetails> {
    try {
      // First, try to get the user data from the users list
      const usersResponse = await this.getUsers({ limit: 100 }); // Get more users to find the one we need
      const userData = usersResponse.users?.find(user => user.user_id === userId);
      
      if (!userData) {
        throw new Error('User not found');
      }

      // Fetch real subscription details from the dedicated admin endpoint
      let subscriptionData: UserSubscription | null = null;
      try {
        const subResp = await api.get<UserSubscription>(`/admin/users/${userId}/subscription`);
        subscriptionData = subResp.data;
      } catch {
        // fall back to users-list data if the endpoint fails
      }

      // Map the available data from the users API to the user details format
      const tierLimits = this.getTierLimits(subscriptionData?.tier || userData.tier || 'free');
      
      return {
        id: userId,
        username: userData.username || `user_${userId}`,
        full_name: userData.full_name,
        email: userData.email,
        telegram_id: userData.telegram_id,
        email_verified: userData.email_verified,
        subscription: {
          user_subscription: subscriptionData ?? {
            tier: userData.tier || 'free',
            period: userData.period || 'monthly',
            is_active: userData.is_active,
            started_at: new Date().toISOString(),
            auto_renew: false,
          },
          effective_plan: {
            tier: subscriptionData?.tier || userData.tier || 'free',
            display_name: this.getTierDisplayName(subscriptionData?.tier || userData.tier || 'free'),
            limits: tierLimits
          }
        },
        usage_today: {
          date: userData.last_date || new Date().toISOString().split('T')[0],
          smart_add_count: userData.smart_add_total || 0,
          try_on_count: userData.try_on_total || 0,
          ai_stylist_count: 0 // We don't have this specific data
        },
        closet: {
          total_items: Math.floor((userData.smart_add_total || 0) / 2), // Estimate based on usage
          limit: tierLimits.max_closet_items,
          remaining: tierLimits.max_closet_items ? 
            Math.max(0, tierLimits.max_closet_items - Math.floor((userData.smart_add_total || 0) / 2)) : 
            null
        },
        is_active: userData.is_active,
        language: 'en',
        created_at: undefined,
        last_login: userData.last_date
      } as UserDetails;
    } catch (error: any) {
      console.warn('Failed to get user details, returning mock data:', error.message);
      // Return mock data as fallback
      return {
        id: userId,
        username: `user_${userId}`,
        subscription: {
          user_subscription: {
            tier: 'free',
            period: 'monthly',
            is_active: true,
            started_at: new Date().toISOString(),
            auto_renew: false
          },
          effective_plan: {
            tier: 'free',
            display_name: 'Free Plan',
            limits: {
              max_closet_items: 10,
              max_smart_add_per_day: 3,
              max_try_on_per_day: 5,
              max_ai_stylist_per_day: 2,
              cloth_item_per_tryon: 2,
              custom_prompt_in_try_on: false,
              quick_ai_stylist: true,
              advanced_ai_stylist: false
            }
          }
        },
        usage_today: {
          date: new Date().toISOString().split('T')[0],
          smart_add_count: 0,
          try_on_count: 0,
          ai_stylist_count: 0
        },
        closet: {
          total_items: 0,
          limit: 10,
          remaining: 10
        },
        is_active: true,
        language: 'en'
      } as UserDetails;
    }
  },

  // Helper method to get tier limits
  getTierLimits(tier: string): SubscriptionLimits {
    const normalizedTier = tier?.toLowerCase();
    const limits: Record<string, SubscriptionLimits> = {
      'free': {
        max_closet_items: 10,
        max_smart_add_per_day: 3,
        max_try_on_per_day: 5,
        max_ai_stylist_per_day: 2,
        cloth_item_per_tryon: 2,
        custom_prompt_in_try_on: false,
        quick_ai_stylist: true,
        advanced_ai_stylist: false
      },
      'bronze': {
        max_closet_items: 50,
        max_smart_add_per_day: 10,
        max_try_on_per_day: 15,
        max_ai_stylist_per_day: 5,
        cloth_item_per_tryon: 3,
        custom_prompt_in_try_on: true,
        quick_ai_stylist: true,
        advanced_ai_stylist: false
      },
      'silver': {
        max_closet_items: 100,
        max_smart_add_per_day: 25,
        max_try_on_per_day: 30,
        max_ai_stylist_per_day: 10,
        cloth_item_per_tryon: 4,
        custom_prompt_in_try_on: true,
        quick_ai_stylist: true,
        advanced_ai_stylist: true
      },
      'gold': {
        max_closet_items: 200,
        max_smart_add_per_day: 50,
        max_try_on_per_day: 60,
        max_ai_stylist_per_day: 20,
        cloth_item_per_tryon: 5,
        custom_prompt_in_try_on: true,
        quick_ai_stylist: true,
        advanced_ai_stylist: true
      },
      'firooze': {
        max_closet_items: undefined, // Unlimited
        max_smart_add_per_day: undefined, // Unlimited
        max_try_on_per_day: undefined, // Unlimited
        max_ai_stylist_per_day: undefined, // Unlimited
        cloth_item_per_tryon: undefined, // Unlimited
        custom_prompt_in_try_on: true,
        quick_ai_stylist: true,
        advanced_ai_stylist: true
      },
      'firoozeh': { // Support both spellings
        max_closet_items: undefined,
        max_smart_add_per_day: undefined,
        max_try_on_per_day: undefined,
        max_ai_stylist_per_day: undefined,
        cloth_item_per_tryon: undefined,
        custom_prompt_in_try_on: true,
        quick_ai_stylist: true,
        advanced_ai_stylist: true
      }
    };
    return limits[normalizedTier] || limits['free'];
  },

  // Helper method to get tier display name
  getTierDisplayName(tier: string): string {
    const normalizedTier = tier?.toLowerCase();
    const names: Record<string, string> = {
      'free': 'Free Plan',
      'bronze': 'Bronze Plan',
      'silver': 'Silver Plan',
      'gold': 'Gold Plan',
      'firooze': 'Firooze Plan',
      'firoozeh': 'Firooze Plan'
    };
    return names[normalizedTier] || 'Unknown Plan';
  },

  // Reports
  async getSubscriptionsSummary() {
    try {
      const response = await api.get('/admin/reports/subscriptions/summary');
      // Transform the backend data to match frontend expectations
      const data = response.data;
      return {
        active_subscriptions: data.total_active || 0,
        total_subscriptions: data.total || 0,
        // Map tier counts from backend format
        free_users: data.by_tier?.free || 0,
        bronze_users: data.by_tier?.bronze || 0,
        silver_users: data.by_tier?.silver || 0,
        gold_users: data.by_tier?.gold || 0,
        firooze_users: data.by_tier?.firooze || 0
      };
    } catch (error: any) {
      console.error('Failed to load subscription summary:', error);
      throw error;
    }
  },

  async getDailyUsage(date?: string) {
    try {
      const response = await api.get('/admin/reports/usage/daily', {
        params: { date }
      });
      // Transform backend data format to match frontend expectations
      const data = response.data;
      return {
        date: data.date,
        total_smart_add: data.totals?.smart_add_total || 0,
        total_try_on: data.totals?.try_on_total || 0,
        total_ai_stylist: 0, // Will be calculated from other metrics when available
        unique_users: data.totals?.users || 0,
        top_users: data.top_users || []
      };
    } catch (error: any) {
      console.error('Failed to load daily usage:', error);
      throw error;
    }
  },

  async getUsageRange(start: string, end: string) {
    const response = await api.get('/admin/reports/usage/range', {
      params: { start, end }
    });
    return response.data;
  },

  // Subscription Plans
  async getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    try {
      const response = await api.get('/admin/subscriptions/plans', {
        params: { include_inactive: true }
      });
      // Handle the new response format
      if (response.data.success && response.data.data) {
        return response.data.data;
      } else if (response.data.data) {
        // Fallback data from backend
        return response.data.data;
      }
      throw new Error('Invalid response format');
    } catch (error: any) {
      console.error('Failed to load subscription plans:', error);
      throw error;
    }
  },

  async getSubscriptionConfig(): Promise<SubscriptionConfig> {
    try {
      const response = await api.get('/admin/subscriptions/config');
      return response.data;
    } catch (error: any) {
      console.error('Failed to load subscription configuration:', error);
      throw error;
    }
  },

  async updateSubscriptionConfig(config: Partial<SubscriptionConfig>): Promise<SubscriptionConfig> {
    try {
      const response = await api.put('/admin/subscriptions/config', config);
      if (response.data.success && response.data.config) {
        return response.data.config;
      }
      return response.data;
    } catch (error: any) {
      console.error('Failed to update subscription configuration:', error);
      throw error;
    }
  },

  async getSubscriptionStats(): Promise<SubscriptionStats> {
    try {
      const response = await api.get('/admin/subscriptions/stats');
      return response.data;
    } catch (error: any) {
      console.error('Failed to load subscription statistics:', error);
      throw error;
    }
  },

  async getActiveSubscriptions(params?: {
    limit?: number;
    offset?: number;
    tier?: string;
    status?: string;
  }): Promise<{ items: UserSubscriptionDetail[]; total: number }> {
    try {
      const response = await api.get('/admin/subscriptions/active', { params });
      return response.data;
    } catch (error: any) {
      console.error('Failed to load active subscriptions:', error);
      throw error;
    }
  },

  async updateSubscriptionPlan(tier: string, plan: Partial<SubscriptionPlan>): Promise<SubscriptionPlan> {
    try {
      const response = await api.put(`/admin/subscriptions/plans/${tier}`, plan);
      if (response.data.success && response.data.plan) {
        return response.data.plan;
      }
      throw new Error('Invalid response format');
    } catch (error: any) {
      console.error('Failed to update subscription plan:', error);
      throw error;
    }
  },

  // ZAR Packages
  async getZarPackages(): Promise<ZarPackage[]> {
    try {
      const response = await api.get('/admin/zar/packages');
      if (response.data.success && response.data.packages) {
        return response.data.packages;
      }
      throw new Error('Invalid response format');
    } catch (error: any) {
      console.error('Failed to load ZAR packages:', error);
      throw error;
    }
  },

  async createZarPackage(packageData: Partial<ZarPackage>): Promise<ZarPackage> {
    try {
      const response = await api.post('/admin/zar/packages', packageData);
      if (response.data.success && response.data.package) {
        return response.data.package;
      }
      throw new Error('Invalid response format');
    } catch (error: any) {
      console.error('Failed to create ZAR package:', error);
      throw error;
    }
  },

  async updateZarPackage(packageId: string, packageData: Partial<ZarPackage>): Promise<ZarPackage> {
    try {
      const response = await api.put(`/admin/zar/packages/${packageId}`, packageData);
      if (response.data.success && response.data.package) {
        return response.data.package;
      }
      throw new Error('Invalid response format');
    } catch (error: any) {
      console.error('Failed to update ZAR package:', error);
      throw error;
    }
  },

  async deleteZarPackage(packageId: string): Promise<boolean> {
    try {
      const response = await api.delete(`/admin/zar/packages/${packageId}`);
      return response.data.success === true;
    } catch (error: any) {
      console.error('Failed to delete ZAR package:', error);
      throw error;
    }
  },

  // User management 
  async createUser(userData: {
    username: string;
    email?: string;
    full_name?: string;
    telegram_id?: string;
  }): Promise<{ success: boolean; user?: any; error?: string }> {
    try {
      const response = await api.post('/admin/users/create', userData);
      return response.data;
    } catch (error: any) {
      console.error('Failed to create user:', error);
      throw error;
    }
  },

  async confirmUserEmail(userId: string): Promise<{ success: boolean; email_verified: boolean }> {
    const response = await api.post(`/admin/users/${userId}/confirm-email`);
    return response.data;
  },

  async updateUserSubscription(
    userId: string,
    tier: string,
    period?: string,
    auto_renew: boolean = false,
    overrideDurationMinutes?: number,
  ) {
    // Admin override endpoint
    const response = await api.post(`/admin/users/${userId}/subscription/override`, {
      tier,
      period: period || 'monthly',
      auto_renew,
      ...(overrideDurationMinutes ? { override_duration_minutes: overrideDurationMinutes } : {}),
    });
    return response.data;
  },

  // User actions timeseries (reports)
  async getUserActionsTimeseries(
    userId: string,
    params?: { start?: string; end?: string; action?: 'add_item' | 'tryon' | 'get_recom'; interval?: 'day' }
  ): Promise<ActionTimeseriesResponse> {
    const response = await api.get(`/admin/users/${userId}/actions/timeseries`, {
      params,
    });
    return response.data as ActionTimeseriesResponse;
  },

  // ZAR wallet transactions
  async getUserZarTransactions(
    userId: string,
    params?: { limit?: number; offset?: number; start?: string; end?: string }
  ): Promise<ZarTransactionsResponse> {
    const response = await api.get(`/admin/users/${userId}/wallets/zar/transactions`, { params });
    return response.data as ZarTransactionsResponse;
  },

  // NOWPayments payments
  async getUserNowPayments(
    userId: string,
    params?: { limit?: number; offset?: number; status_filter?: string; payment_type?: string; start?: string; end?: string }
  ): Promise<NowPaymentsListResponse> {
    const response = await api.get(`/admin/users/${userId}/payments/nowpayments`, { params });
    return response.data as NowPaymentsListResponse;
  },

  // Admin manual ZAR transaction
  async createAdminZarTransaction(userId: string, payload: AdminZarTxRequest): Promise<AdminZarTxResponse> {
    const response = await api.post(`/admin/users/${userId}/wallets/zar/transaction`, payload);
    return response.data as AdminZarTxResponse;
  },

  /** Subscription tier-change audit log for a user (events, not invoices). */
  async getUserSubscriptionHistory(
    userId: string,
    params?: { limit?: number; skip?: number; start_date?: string; end_date?: string }
  ): Promise<SubscriptionEventsResponse> {
    const response = await api.get(`/admin/users/${userId}/subscription/history`, {
      params: {
        limit: params?.limit ?? 20,
        skip: params?.skip ?? 0,
        start_date: params?.start_date,
        end_date: params?.end_date,
      },
    });
    return response.data as SubscriptionEventsResponse;
  },

  // Analytics
  async getComprehensiveAnalytics(startDate?: string, endDate?: string) {
    try {
      const params: any = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await api.get('/admin/analytics/comprehensive', { params });
      return response.data;
    } catch (error: any) {
      console.error('Failed to load comprehensive analytics:', error);
      throw error;
    }
  },

  // Tier Limitations Management
  async getTierLimitations(): Promise<TierLimitationsResponse> {
    try {
      const response = await api.get('/admin/subscription/tier-limitations');
      return response.data;
    } catch (error: any) {
      console.error('Failed to load tier limitations:', error);
      throw error;
    }
  },

  async updateTierLimitations(tierLimitations: Record<string, TierLimitation>, updatedBy?: string): Promise<any> {
    try {
      const payload = {
        tier_limitations: tierLimitations,
        updated_by: updatedBy || 'admin'
      };
      const response = await api.put('/admin/subscription/tier-limitations', payload);
      return response.data;
    } catch (error: any) {
      console.error('Failed to update tier limitations:', error);
      throw error;
    }
  },

  async validateTierLimitations(tierLimitations: Record<string, TierLimitation>): Promise<any> {
    try {
      const response = await api.post('/admin/subscription/tier-limitations/validate', {
        tier_limitations: tierLimitations
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to validate tier limitations:', error);
      throw error;
    }
  },

  async resetTierLimitationsToDefault(): Promise<any> {
    try {
      const response = await api.post('/admin/subscriptions/tier-limitations/reset');
      return response.data;
    } catch (error: any) {
      console.error('Failed to reset tier limitations:', error);
      throw error;
    }
  },

  // Try-On Feedback Management
  async getTryOnFeedback(params?: {
    limit?: number;
    offset?: number;
    status?: string;
    sort_by?: string;
    sort_order?: string;
  }): Promise<TryOnFeedbackListResponse> {
    try {
      const response = await api.get('/admin/tryon-feedback', { params });
      return response.data;
    } catch (error: any) {
      console.error('Failed to load try-on feedback:', error);
      throw error;
    }
  },

  async getTryOnFeedbackDetails(feedbackId: string): Promise<TryOnFeedbackDetail> {
    try {
      const response = await api.get(`/admin/tryon-feedback/${feedbackId}`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to load feedback details:', error);
      throw error;
    }
  },

  async approveTryOnFeedback(
    feedbackId: string,
    data: { admin_id?: string; review_notes?: string; refund_amount?: number }
  ): Promise<any> {
    try {
      const response = await api.post(`/admin/tryon-feedback/${feedbackId}/approve`, data);
      return response.data;
    } catch (error: any) {
      console.error('Failed to approve feedback:', error);
      throw error;
    }
  },

  async rejectTryOnFeedback(
    feedbackId: string,
    data: { admin_id?: string; review_notes: string }
  ): Promise<any> {
    try {
      const response = await api.post(`/admin/tryon-feedback/${feedbackId}/reject`, data);
      return response.data;
    } catch (error: any) {
      console.error('Failed to reject feedback:', error);
      throw error;
    }
  },

  async processTryOnRefund(feedbackId: string): Promise<any> {
    try {
      const response = await api.post(`/admin/tryon-feedback/${feedbackId}/refund`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to process refund:', error);
      throw error;
    }
  },

  // Survey Management
  async getSurveys(params?: {
    limit?: number;
    offset?: number;
    sort_by?: string;
    sort_order?: string;
    start_date?: string;
    end_date?: string;
    min_rating?: number;
    max_rating?: number;
  }): Promise<SurveyListResponse> {
    try {
      const response = await api.get('/admin/surveys', { params });
      return response.data;
    } catch (error: any) {
      console.error('Failed to load surveys:', error);
      throw error;
    }
  },

  async getSurveyDetails(surveyId: string): Promise<SurveyDetailResponse> {
    try {
      const response = await api.get(`/admin/surveys/${surveyId}`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to load survey details:', error);
      throw error;
    }
  },

  // Action Costs Management
  async getActionCosts(): Promise<{ success: boolean; currency: string; costs: Array<{ action: string; cost: number; description: string; currency: string }> }> {
    try {
      const response = await api.get('/admin/action-costs');
      if (response.data.success) {
        return response.data;
      }
      throw new Error('Invalid response format');
    } catch (error: any) {
      console.error('Failed to load action costs:', error);
      throw error;
    }
  },

  async updateActionCost(action: string, cost: number): Promise<any> {
    try {
      const response = await api.put(`/admin/action-costs/${action}`, { cost });
      if (response.data.success) {
        return response.data;
      }
      throw new Error('Invalid response format');
    } catch (error: any) {
      console.error('Failed to update action cost:', error);
      throw error;
    }
  },

  // Note: Mock data methods removed - now using real backend data

  // =============== QUEST MANAGEMENT ===============

  async getQuests(params?: {
    is_onboarding?: boolean;
    is_active?: boolean;
    quest_type?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ quests: AdminQuest[]; total: number }> {
    const response = await api.get('/admin/quests', { params });
    const data = response.data;
    return {
      quests: data.quests ?? [],
      total: data.count ?? data.total ?? (data.quests ?? []).length,
    };
  },

  async getQuest(questId: string): Promise<AdminQuest> {
    const response = await api.get(`/admin/quests/${questId}`);
    return response.data.quest ?? response.data;
  },

  async createQuest(quest: CreateQuestPayload): Promise<AdminQuest> {
    const response = await api.post('/admin/quests', quest);
    return response.data.quest ?? response.data;
  },

  async updateQuest(questId: string, quest: Partial<CreateQuestPayload>): Promise<AdminQuest> {
    const response = await api.put(`/admin/quests/${questId}`, quest);
    return response.data.quest ?? response.data;
  },

  async deleteQuest(questId: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/admin/quests/${questId}`);
    return response.data;
  },

  async reorderQuests(questOrders: { quest_id: string; order: number }[]): Promise<{ success: boolean; updated: number }> {
    // Backend expects order as a query param on a per-quest endpoint
    const results = await Promise.all(
      questOrders.map(({ quest_id, order }) =>
        api.post(`/admin/quests/${quest_id}/reorder`, null, { params: { order } })
      )
    );
    return { success: true, updated: results.length };
  },

  async getQuestStats(): Promise<QuestStats> {
    const response = await api.get('/admin/quests/stats');
    const data = response.data;
    // Backend returns { success, stats: [{ quest_id, total_started, total_completed, completion_rate }] }
    const completionStats: QuestStats['completion_stats'] = (data.stats ?? []).map((s: any) => ({
      quest_id: s.quest_id,
      title: s.title ?? s.quest_id,
      total_started: s.total_started ?? 0,
      total_completed: s.total_completed ?? 0,
      completion_rate: s.completion_rate ?? 0,
      avg_completion_time_hours: s.avg_completion_time_hours,
    }));

    const overallRate =
      completionStats.length > 0
        ? completionStats.reduce((sum, s) => sum + s.completion_rate, 0) / completionStats.length
        : 0;

    return {
      // Use server-provided counts (active non-deleted quests only).
      // Never fall back to completionStats.length — that aggregates quest_progress
      // records and would include deleted quests' historical progress entries.
      total_quests: data.total_quests ?? data.active_quests ?? 0,
      active_quests: data.active_quests ?? data.total_quests ?? 0,
      onboarding_quests: data.onboarding_quests ?? 0,
      completion_stats: completionStats,
      overall_completion_rate: overallRate,
      total_users_with_quests: data.total_users_with_quests ?? 0,
      users_completed_all: data.users_completed_all ?? 0,
    };
  },

  async getUserQuestProgress(userId: string): Promise<{
    user_id: string;
    quests: Array<{
      quest: AdminQuest;
      progress: QuestProgressItem | null;
      status: string;
      current_progress: number;
      is_completed: boolean;
    }>;
    summary: {
      total_quests: number;
      completed_quests: number;
      progress_percentage: number;
      is_onboarding_complete: boolean;
    };
  }> {
    const response = await api.get(`/admin/quests/user/${userId}/progress`);
    const data = response.data;
    return {
      user_id: userId,
      quests: data.quests ?? [],
      summary: data.summary ?? {
        total_quests: 0,
        completed_quests: 0,
        progress_percentage: 0,
        is_onboarding_complete: false,
      },
    };
  },

  async resetUserQuestProgress(userId: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/admin/quests/user/${userId}/reset`);
    return response.data;
  },

  async getQuestAnalytics(): Promise<QuestAnalytics> {
    // Fetch all analytics sub-resources in parallel
    const [completionRes, funnelRes, timingRes, dashboardRes] = await Promise.allSettled([
      api.get('/admin/quests/analytics/completion'),
      api.get('/admin/quests/analytics/funnel'),
      api.get('/admin/quests/analytics/timing'),
      api.get('/admin/quests/analytics/dashboard'),
    ]);

    const completion_rates =
      completionRes.status === 'fulfilled'
        ? completionRes.value.data.completion_rates ?? []
        : [];
    const onboarding_funnel =
      funnelRes.status === 'fulfilled'
        ? funnelRes.value.data.funnel ?? []
        : [];
    const timing_analytics =
      timingRes.status === 'fulfilled'
        ? timingRes.value.data.timing ?? []
        : [];
    const dashboardData =
      dashboardRes.status === 'fulfilled' ? dashboardRes.value.data : {};

    return {
      completion_rates,
      onboarding_funnel,
      timing_analytics,
      dashboard_summary: {
        total_active_users: dashboardData.total_active_users ?? 0,
        total_completed_all: dashboardData.total_completed_all ?? 0,
        overall_completion_rate: dashboardData.overall_completion_rate ?? 0,
        avg_quests_per_user: dashboardData.avg_quests_per_user ?? 0,
        most_completed_quest: dashboardData.most_completed_quest,
        least_completed_quest: dashboardData.least_completed_quest,
      },
    };
  },

  // ── Tour Management ──────────────────────────────────────────────────────

  async getTours(params?: { tour_type?: TourType; is_active?: boolean; include_deleted?: boolean }): Promise<{ tours: AdminTour[]; count: number }> {
    const response = await api.get('/admin/tours', { params });
    return { tours: response.data.tours ?? [], count: response.data.count ?? 0 };
  },

  async getTour(tourId: string): Promise<AdminTour> {
    const response = await api.get(`/admin/tours/${tourId}`);
    return response.data.tour;
  },

  async createTour(payload: CreateTourPayload): Promise<AdminTour> {
    const response = await api.post('/admin/tours', payload);
    return response.data.tour;
  },

  async updateTour(tourId: string, payload: UpdateTourPayload): Promise<AdminTour> {
    const response = await api.put(`/admin/tours/${tourId}`, payload);
    return response.data.tour;
  },

  async deleteTour(tourId: string, hardDelete = false): Promise<{ success: boolean; deleted: string }> {
    const response = await api.delete(`/admin/tours/${tourId}`, { params: { hard_delete: hardDelete } });
    return response.data;
  },
};

export default api;

// =============== INVOICE & PAYMENT MANAGEMENT ===============

export interface Invoice {
  id: string;
  invoice_number: string;
  user_id: string;
  status: 'open' | 'pending' | 'paid' | 'failed' | 'expired';
  type: 'subscription' | 'subscription_new' | 'subscription_renewal' | 'wallet_topup';
  total_amount: number;
  currency: string;
  zar_amount?: number;
  subscription_tier?: string;
  subscription_period?: string;
  customer_name?: string;
  customer_email: string;
  billing_address?: Record<string, any>;
  description?: string;
  created_at: string;
  expires_at: string;
  paid_at?: string;
  payment_count?: number;
}

export interface Payment {
  id: string;
  order_id: string;
  provider: 'yekpay' | 'nowpayments';
  type: 'wallet_topup' | 'subscription';
  user_id: string;
  status: string;
  credited: boolean;
  amount?: number;
  price_amount: number;
  price_currency: string;
  invoice_id?: string;
  external_invoice_id?: string;
  invoice_url?: string;
  subscription_tier?: string;
  subscription_period?: string;
  created_at: string;
  processed_at?: string;
  last_webhook?: Record<string, any>;
}

export interface PaymentStats {
  period: {
    start: string;
    end: string;
  };
  payments: {
    total: number;
    credited: number;
    uncredited: number;
    by_provider: {
      yekpay: number;
      nowpayments: number;
    };
    by_type: {
      wallet_topup: number;
      subscription: number;
    };
    by_status: {
      created: number;
      finished: number;
      failed: number;
    };
  };
  invoices: {
    total: number;
    paid: number;
    pending: number;
    open: number;
    failed: number;
  };
  revenue: {
    total_usd: number;
    total_eur: number;
  };
}

export interface InvoiceDetail extends Invoice {
  payments: Payment[];
  user: {
    id: string;
    telegram_id?: string;
    username?: string;
    email?: string;
  };
}

// Fetch invoices with filters
export const fetchInvoices = async (params?: {
  limit?: number;
  skip?: number;
  status_filter?: string;
  type_filter?: string;
  user_id?: string;
  start_date?: string;
  end_date?: string;
}): Promise<{ success: boolean; total: number; invoices: Invoice[] }> => {
  const response = await api.get('/admin/invoices', { params });
  return response.data;
};

// Fetch single invoice with details
export const fetchInvoiceDetail = async (invoiceId: string): Promise<InvoiceDetail> => {
  const response = await api.get(`/admin/invoices/${invoiceId}`);
  return response.data.invoice;
};

// Fetch payments with filters
export const fetchPayments = async (params?: {
  limit?: number;
  skip?: number;
  provider?: string;
  status_filter?: string;
  type_filter?: string;
  user_id?: string;
  invoice_id?: string;
  credited_only?: boolean;
  start_date?: string;
  end_date?: string;
}): Promise<{ success: boolean; total: number; payments: Payment[] }> => {
  const response = await api.get('/admin/payments', { params });
  return response.data;
};

// Fetch payment statistics
export const fetchPaymentStats = async (params?: {
  start_date?: string;
  end_date?: string;
}): Promise<PaymentStats> => {
  const response = await api.get('/admin/payments/stats/summary', { params });
  return response.data;
};

// Manual payment verification (for testing)
export const manuallyVerifyPayment = async (invoiceId: string, userToken: string): Promise<any> => {
  const response = await axios.post(
    `${API_BASE_URL}/payments/now/manual-verify/${invoiceId}`,
    {},
    {
      headers: {
        'Authorization': `Bearer ${userToken}`
      }
    }
  );
  return response.data;
};


// =============== MANUAL STATUS MANAGEMENT (ADMIN) ===============

// Create manual payment for an invoice
export const createManualPayment = async (invoiceId: string, description?: string): Promise<any> => {
  const response = await api.post(`/admin/invoices/${invoiceId}/create-manual-payment`, {
    description: description
  });
  return response.data;
};

// Mark invoice as paid manually
export const markInvoicePaid = async (
  invoiceId: string, 
  paymentId?: string,
  processPayment: boolean = true
): Promise<any> => {
  const response = await api.post(`/admin/invoices/${invoiceId}/mark-paid`, {
    payment_id: paymentId || 'admin_manual',
    process_payment: processPayment
  });
  return response.data;
};

// Mark invoice as failed manually
export const markInvoiceFailed = async (invoiceId: string): Promise<any> => {
  const response = await api.post(`/admin/invoices/${invoiceId}/mark-failed`);
  return response.data;
};

// Mark payment as credited (flag only - doesn't process)
export const markPaymentCredited = async (orderId: string): Promise<any> => {
  const response = await api.post(`/admin/payments/${orderId}/mark-credited`);
  return response.data;
};

// Process payment manually (credits wallet/activates subscription)
export const processPaymentManually = async (orderId: string): Promise<any> => {
  const response = await api.post(`/admin/payments/${orderId}/process-payment`);
  return response.data;
};

// =============== INSTAGRAM BATCH PROCESSING (ADMIN) ===============

export interface InstagramPost {
  id: string;
  image_url: string;
  image_urls?: string[];  // All image URLs for carousel posts
  thumbnail_url?: string;
  caption?: string;
  timestamp?: string;
  post_url?: string;
  is_carousel?: boolean;
}

export interface InstagramPostsResponse {
  success: boolean;
  username: string;
  posts: InstagramPost[];
  total: number;
  next_max_id?: string;  // Pagination cursor for next page
  note?: string;
}

export interface PendingSource {
  id?: string;
  user_id: string;
  source_index: number;
  source_image_id: string;
  status?: 'pending' | 'ready' | 'error';
  retry_count?: number;
  created_at?: string;
  updated_at?: string;
  pending_items_count?: number; // Number of pending items for this source
}

export interface PendingItem {
  id: string;
  user_id: string;
  batch_id?: string;
  image_id: string;
  source_image_id?: string;
  source_index?: number;
  meta_data?: {
    name?: string;
    category?: string;
    color?: string;
    size?: string;
    brand?: string;
    material?: string;
    season?: string;
    style?: string;
    pattern?: string;
    occasion?: string;
    notes?: string;
    // New standardized fields
    category_level_1?: string;
    category_level_2?: string;
    body_zone?: string;
    main_color?: string;
    secondary_colors?: string[];
    style_keyword?: string;
    material_main?: string;
    formality_score?: number;
    length_detail?: string;
    closure_type?: string;
    special_features?: string[];
    fit_type?: string;
  };
  confidence?: number; // Top-level confidence field from backend
  similarity_info?: Record<string, any>;
  status: 'pending' | 'accepted' | 'discarded';
  created_at?: string;
  updated_at?: string;
}

// Get Instagram posts for a username
export const getInstagramPosts = async (
  instagramUsername: string,
  limit: number = 5,
  offset: number = 0,
  max_id?: string  // Pagination cursor from previous response
): Promise<InstagramPostsResponse> => {
  try {
    const params: { limit: number; offset: number; max_id?: string } = { limit, offset };
    if (max_id) {
      params.max_id = max_id;
    }
    const response = await api.get(`/admin/instagram/${instagramUsername}/posts`, {
      params
    });
    return response.data;
  } catch (error: any) {
    // Re-throw with rate limit info if applicable
    if (error?.response?.status === 429 || error?.response?.data?.rate_limited) {
      const enhancedError: any = new Error(error?.response?.data?.message || 'Instagram rate limit reached');
      enhancedError.response = error.response;
      enhancedError.response.data = {
        ...error.response.data,
        rate_limited: true
      };
      throw enhancedError;
    }
    throw error;
  }
};

// Admin batch process images (from URLs or files)
export const adminBatchProcessImages = async (
  userId: string,
  imageUrls?: string[],
  files?: File[]
): Promise<{ success: boolean; message: string; total_images: number; user_id: string }> => {
  // If we have URLs, send as JSON
  if (imageUrls && imageUrls.length > 0 && (!files || files.length === 0)) {
    const response = await api.post(
      `/admin/users/${userId}/batch/process/admin`,
      { image_urls: imageUrls }
    );
    return response.data;
  }
  
  // If we have files, send as form data
  if (files && files.length > 0) {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    
    const response = await api.post(
      `/admin/users/${userId}/batch/process/admin`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }
  
  throw new Error('Either imageUrls or files must be provided');
};

// Get pending items for a user (admin)
export const getPendingItems = async (
  userId: string,
  limit: number = 200,
  sourceImageId?: string
): Promise<PendingItem[]> => {
  const params: { limit: number; source_image_id?: string } = { limit };
  if (sourceImageId) {
    params.source_image_id = sourceImageId;
  }
  const response = await api.get(`/admin/users/${userId}/pending`, {
    params
  });
  return response.data;
};

// Get pending sources for a user (admin)
export const getPendingSources = async (
  userId: string
): Promise<PendingSource[]> => {
  const response = await api.get(`/admin/users/${userId}/pending/sources`);
  return response.data;
};

// Delete pending sources (admin)
export const deletePendingSources = async (
  userId: string,
  sourceImageIds: string[]
): Promise<{ success: boolean; deleted: number }> => {
  const response = await api.post(`/admin/users/${userId}/pending/sources/delete`, {
    source_image_ids: sourceImageIds
  });
  return response.data;
};

// Update a pending item (admin)
export const updatePendingItem = async (
  userId: string,
  pendingItemId: string,
  updateData: { meta_data?: Partial<PendingItem['meta_data']> }
): Promise<PendingItem> => {
  const response = await api.put(
    `/admin/users/${userId}/pending/${pendingItemId}`,
    updateData
  );
  return response.data;
};

// Confirm pending items (admin)
export interface ConfirmItemPayload {
  image_id: string;
  closet_id?: string;
  meta_data?: PendingItem['meta_data'];
  similarity_info?: Record<string, any>;
  force_add?: boolean;
}

export const confirmPendingItems = async (
  userId: string,
  items: ConfirmItemPayload[]
): Promise<{ success: boolean; items: any[]; count: number }> => {
  const response = await api.post(`/admin/users/${userId}/pending/confirm`, {
    items
  });
  return response.data;
};

// ---------------------------------------------------------------------------
// Job Management
// ---------------------------------------------------------------------------

export type JobRunStatus = 'running' | 'success' | 'failed' | 'skipped';

export interface JobRun {
  id: string;
  job_id: string;
  status: JobRunStatus;
  started_at: string;
  finished_at?: string;
  duration_seconds?: number;
  records_processed: number;
  records_failed: number;
  error_message?: string;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface JobInfo {
  job_id: string;
  description: string;
  schedule: string;
  enabled: boolean;
  status?: JobRunStatus;
  last_run_at?: string;
  last_run_duration_seconds?: number;
  last_run_records_processed?: number;
  last_run_error?: string;
  next_run_at?: string;
}

export interface JobRunsResponse {
  job_id: string;
  total: number;
  skip: number;
  limit: number;
  runs: JobRun[];
}

export const jobApi = {
  listJobs: async (): Promise<{ jobs: JobInfo[] }> => {
    const response = await api.get('/admin/jobs');
    return response.data;
  },

  triggerJob: async (jobId: string): Promise<{ success: boolean; job_id: string; run_id: string }> => {
    const response = await api.post(`/admin/jobs/${jobId}/trigger`);
    return response.data;
  },

  enableJob: async (jobId: string): Promise<{ success: boolean; job_id: string; enabled: boolean }> => {
    const response = await api.post(`/admin/jobs/${jobId}/enable`);
    return response.data;
  },

  disableJob: async (jobId: string): Promise<{ success: boolean; job_id: string; enabled: boolean }> => {
    const response = await api.post(`/admin/jobs/${jobId}/disable`);
    return response.data;
  },

  getJobRuns: async (jobId: string, limit = 20, skip = 0): Promise<JobRunsResponse> => {
    const response = await api.get(`/admin/jobs/${jobId}/runs`, { params: { limit, skip } });
    return response.data;
  },

  getJobRun: async (runId: string): Promise<JobRun> => {
    const response = await api.get(`/admin/jobs/runs/${runId}`);
    return response.data;
  },
};

